import { z } from 'zod';

import type { ApiResult } from './client';
import { get, post } from './client';
import {
  adminSubscriptionListSchema,
  adminSubscriptionSchema,
} from './_schemas';

import type { AdminSubscription } from '@/types/api';

/**
 * Fleet view — backed by `/admin/subscriptions`. Returns the
 * denormalised AdminSubscription shape (client_email + broker_display_name
 * + strategy_code_name) so the dashboard renders one row per
 * subscription without N+1 calls. The plain `Subscription` type stays
 * for per-user shapes that don't see admin fields.
 */

/** Server-side filter/pagination surface for `/admin/subscriptions`. The
 * backend reads exactly these query keys — keep them 1:1 with the route. */
export interface ListSubscriptionsQuery {
  client_id?: string;
  broker_account_id?: string;
  strategy_id?: string;
  active?: boolean;
  mode?: 'live' | 'paper';
  q?: string;
  limit?: number;
  offset?: number;
}

// Back-compat overload: legacy call sites call `listSubscriptions(signal)`
// (AbortSignal as the first positional arg). The new contract is
// `listSubscriptions(query, signal)`. Both resolve to the same impl, which
// detects an AbortSignal in the first slot and treats it as the signal.
export function listSubscriptions(
  signal?: AbortSignal,
): Promise<ApiResult<AdminSubscription[]>>;
export function listSubscriptions(
  query: ListSubscriptionsQuery,
  signal?: AbortSignal,
): Promise<ApiResult<AdminSubscription[]>>;
export function listSubscriptions(
  queryOrSignal: ListSubscriptionsQuery | AbortSignal = {},
  maybeSignal?: AbortSignal,
): Promise<ApiResult<AdminSubscription[]>> {
  const isSignal =
    typeof AbortSignal !== 'undefined' && queryOrSignal instanceof AbortSignal;
  const query: ListSubscriptionsQuery = isSignal ? {} : (queryOrSignal as ListSubscriptionsQuery);
  const signal: AbortSignal | undefined = isSignal
    ? (queryOrSignal as AbortSignal)
    : maybeSignal;
  return get('/admin/subscriptions', adminSubscriptionListSchema, {
    signal,
    query: {
      client_id: query.client_id,
      broker_account_id: query.broker_account_id,
      strategy_id: query.strategy_id,
      active: query.active,
      mode: query.mode,
      q: query.q,
      limit: query.limit,
      offset: query.offset,
    },
  }) as Promise<ApiResult<AdminSubscription[]>>;
}

/** Paged envelope read off `X-Total-Count`. Use this when you need an
 * honest "X of Y" pagination counter. Falls back to `items.length` when
 * the header is absent (defensive — server should always set it). */
export interface PagedSubscriptions {
  items: AdminSubscription[];
  total: number;
}

export const listSubscriptionsPaged = async (
  query: ListSubscriptionsQuery = {},
  signal?: AbortSignal,
): Promise<ApiResult<PagedSubscriptions>> => {
  const r = await listSubscriptions(query, signal);
  const headerTotal = r.meta.headers?.['x-total-count'];
  const total =
    headerTotal !== undefined && headerTotal !== '' ? Number(headerTotal) : r.data.length;
  return {
    data: { items: r.data, total: Number.isFinite(total) ? total : r.data.length },
    meta: r.meta,
  };
};

/* ============================================================================
 * Coverage matrix — strategies × accounts grid of subscription counts.
 * Backed by `/admin/subscriptions/coverage`. Tolerant of nulls the same way
 * `adminSubscriptionSchema` is (display_name, client_email, code_name may be
 * null on partially-populated rows).
 * ============================================================================ */

export const subscriptionCoverageSchema = z.object({
  strategies: z.array(
    z.object({
      id: z.string(),
      code_name: z.string().nullable(),
    }),
  ),
  accounts: z.array(
    z.object({
      id: z.string(),
      broker_type: z.string().nullable(),
      display_name: z.string().nullable(),
      client_id: z.string().nullable(),
      client_email: z.string().nullable(),
    }),
  ),
  cells: z.array(
    z.object({
      strategy_id: z.string(),
      broker_account_id: z.string(),
      active_count: z.number().int(),
      paused_count: z.number().int(),
      total_count: z.number().int(),
      total_capital: z.number(),
    }),
  ),
  total_strategies: z.number().int(),
  total_accounts: z.number().int(),
});
export type SubscriptionCoverage = z.infer<typeof subscriptionCoverageSchema>;

/** Filter surface for the coverage grid. The backend reads exactly
 * `client_id`, `mode`, `active`. */
export interface SubscriptionCoverageQuery {
  client_id?: string;
  mode?: 'live' | 'paper';
  active?: boolean;
}

export const getSubscriptionCoverage = (
  query: SubscriptionCoverageQuery = {},
  signal?: AbortSignal,
): Promise<ApiResult<SubscriptionCoverage>> =>
  get('/admin/subscriptions/coverage', subscriptionCoverageSchema, {
    signal,
    query: {
      client_id: query.client_id,
      mode: query.mode,
      active: query.active,
    },
  }) as Promise<ApiResult<SubscriptionCoverage>>;

/**
 * Operator-surface pause/resume for ONE subscription (admin auth — NOT the
 * client `/users/me/*` surface). Tears down / re-activates that subscription's
 * executor independently of its deployment. Returns the updated row.
 */
export const pauseSubscription = (
  id: string,
  reason: string,
  signal?: AbortSignal,
): Promise<ApiResult<AdminSubscription>> =>
  post(`/admin/subscriptions/${encodeURIComponent(id)}/pause`, adminSubscriptionSchema, { reason }, { signal }) as Promise<
    ApiResult<AdminSubscription>
  >;

export const resumeSubscription = (
  id: string,
  reason: string,
  signal?: AbortSignal,
): Promise<ApiResult<AdminSubscription>> =>
  post(`/admin/subscriptions/${encodeURIComponent(id)}/resume`, adminSubscriptionSchema, { reason }, { signal }) as Promise<
    ApiResult<AdminSubscription>
  >;
